/**
 * Validates and normalizes job field values to ensure they match allowed enum values
 */

import {
  filterBenefitsToFinlandWhitelist,
  normalizeJobTypeForLocale,
  normalizeRemotenessForLocale,
  normalizeStandaloneRemoteInLocationArrays,
} from './processor-output-fields';
import { normalizeJobFieldLabelsOnJob } from '../text/normalize-job-field-label';

export interface JobWithValidatableFields {
  location_en?: string[];
  location_sv?: string[];
  location_fi?: string[];
  benefits_en?: string[];
  benefits_sv?: string[];
  benefits_fi?: string[];
  remoteness_en?: string;
  remoteness_sv?: string;
  remoteness_fi?: string;
  jobType_en?: string;
  jobType_sv?: string;
  jobType_fi?: string;
  normalisedRoleLevel_en?: string;
  normalisedRoleLevel_sv?: string;
  normalisedRoleLevel_fi?: string;
  peopleManagementTier_en?: string;
  peopleManagementTier_sv?: string;
  peopleManagementTier_fi?: string;
  requiredLanguages_en?: string[];
  requiredLanguages_sv?: string[];
  requiredLanguages_fi?: string[];
  optionalLanguages_en?: string[];
  optionalLanguages_sv?: string[];
  optionalLanguages_fi?: string[];
  educationLevel_en?: string;
  educationLevel_sv?: string;
  educationLevel_fi?: string;
  [key: string]: any;
}

// Valid education level values
export const VALID_EDUCATION_LEVELS = {
  en: ['No degree required', 'High school', 'Bachelor', 'Masters', 'Doctorate', 'Apprenticeship', 'Diploma'],
  sv: ['Ingen utbildning krävs', 'Gymnasieutbildning', 'Kandidat', 'Masters', 'Doktorand', 'Lärlingsutbildning', 'Diplom'],
  fi: ['Ei koulutusta vaadita', 'Toisen asteen koulutus', 'Alempi korkeakoulututkinto', 'Ylempi korkeakoulututkinto', 'Tieteellinen jatkotutkinto', 'Oppisopimuskoulutus', 'Diplomi'],
} as const;

// Common language names (not exhaustive, but covers most cases)
export const VALID_LANGUAGE_NAMES = new Set([
  // Major languages
  'English', 'Finnish', 'Swedish', 'German', 'French', 'Spanish', 'Italian', 'Portuguese', 'Dutch', 'Russian',
  'Polish', 'Czech', 'Hungarian', 'Romanian', 'Bulgarian', 'Croatian', 'Serbian', 'Slovak', 'Slovenian',
  'Danish', 'Norwegian', 'Icelandic', 'Estonian', 'Latvian', 'Lithuanian', 'Greek', 'Turkish', 'Arabic',
  'Hebrew', 'Chinese', 'Japanese', 'Korean', 'Hindi', 'Thai', 'Vietnamese', 'Indonesian', 'Malay',
  // Finnish translations
  'Englanti', 'Suomi', 'Ruotsi', 'Saksa', 'Ranska', 'Espanja', 'Italia', 'Portugali', 'Hollanti', 'Venäjä',
  'Puola', 'Tšekki', 'Unkari', 'Romania', 'Bulgaria', 'Kroatia', 'Serbia', 'Slovakia', 'Slovenia',
  'Tanska', 'Norja', 'Islanti', 'Viro', 'Latvia', 'Liettua', 'Kreikka', 'Turkki', 'Arabia',
  'Heprea', 'Kiina', 'Japani', 'Korea', 'Hindi', 'Thai', 'Vietnam', 'Indonesia', 'Malaiji',
  // Swedish translations
  'Engelska', 'Finska', 'Svenska', 'Tyska', 'Franska', 'Spanska', 'Italienska', 'Portugisiska', 'Nederländska', 'Ryska',
  'Polska', 'Tjeckiska', 'Ungerska', 'Rumänska', 'Bulgarianska', 'Kroatiska', 'Serbiska', 'Slovakiska', 'Slovenska',
  'Danska', 'Norska', 'Isländska', 'Estniska', 'Lettiska', 'Litauiska', 'Grekiska', 'Turkiska', 'Arabiska',
  'Hebreiska', 'Kinesiska', 'Japanska', 'Koreanska', 'Hindi', 'Thai', 'Vietnamesiska', 'Indonesiska', 'Malajiska',
]);

// Valid normalised role level values
export const VALID_ROLE_LEVELS = {
  en: ['Intern', 'Junior', 'Mid-Level', 'Senior', 'Expert', 'Manager', 'Director', 'Executive'],
  sv: ['Praktikant', 'Junior', 'Medelnivå', 'Senior', 'Expert', 'Chef', 'Direktör', 'Företagsledare'],
  fi: ['Harjoittelija', 'Junior ja entry-taso', 'Keskitaso', 'Senior-taso', 'Asiantuntija', 'Manageri', 'Johtaja', 'Johto'],
} as const;

// Cross-locale mapping: English → Finnish/Swedish for role levels
const ROLE_LEVEL_EN_TO_FI: Record<string, string> = {
  'Intern': 'Harjoittelija',
  'Junior': 'Junior ja entry-taso',
  'Mid-Level': 'Keskitaso',
  'Senior': 'Senior-taso',
  'Expert': 'Asiantuntija',
  'Manager': 'Manageri',
  'Director': 'Johtaja',
  'Executive': 'Johto',
};
const ROLE_LEVEL_EN_TO_SV: Record<string, string> = {
  'Intern': 'Praktikant',
  'Junior': 'Junior',
  'Mid-Level': 'Medelnivå',
  'Senior': 'Senior',
  'Expert': 'Expert',
  'Manager': 'Chef',
  'Director': 'Direktör',
  'Executive': 'Företagsledare',
};

// Valid people management tier values
export const VALID_PEOPLE_MANAGEMENT = {
  en: ['No People Management', 'People Manager', 'Manager of Managers'],
  sv: ['Ingen personalhantering', 'Personalledare', 'Chef för chefer'],
  fi: ['Ei henkilöstöjohtamista', 'Henkilöstöjohtaja', 'Johtajien johtaja'],
} as const;

// Cross-locale mapping: English → Finnish/Swedish for people management
const PEOPLE_MGMT_EN_TO_FI: Record<string, string> = {
  'No People Management': 'Ei henkilöstöjohtamista',
  'People Manager': 'Henkilöstöjohtaja',
  'Manager of Managers': 'Johtajien johtaja',
};
const PEOPLE_MGMT_EN_TO_SV: Record<string, string> = {
  'No People Management': 'Ingen personalhantering',
  'People Manager': 'Personalledare',
  'Manager of Managers': 'Chef för chefer',
};

function capitalizeWordsForFacet(s: string): string {
  return s
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => (w.length ? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase() : w))
    .join(' ');
}

/** Maps lowercase URL facet values back to Algolia strings (paired with lowercased query params in URLs). */
export function resolveRoleLevelFromUrl(value: string, locale: 'en' | 'fi' | 'sv'): string {
  const trimmed = value.trim();
  if (!trimmed) return trimmed;
  const lower = trimmed.toLowerCase();
  for (const v of VALID_ROLE_LEVELS[locale]) {
    if (v.toLowerCase() === lower) return v;
  }
  const enMatch = VALID_ROLE_LEVELS.en.find((v) => v.toLowerCase() === lower);
  if (enMatch) {
    if (locale === 'fi' && ROLE_LEVEL_EN_TO_FI[enMatch]) return ROLE_LEVEL_EN_TO_FI[enMatch];
    if (locale === 'sv' && ROLE_LEVEL_EN_TO_SV[enMatch]) return ROLE_LEVEL_EN_TO_SV[enMatch];
    if (locale === 'en') return enMatch;
  }
  return capitalizeWordsForFacet(trimmed);
}

export function resolveEducationLevelFromUrl(value: string, locale: 'en' | 'fi' | 'sv'): string {
  const trimmed = value.trim();
  if (!trimmed) return trimmed;
  const lower = trimmed.toLowerCase();
  for (const v of VALID_EDUCATION_LEVELS[locale]) {
    if (v.toLowerCase() === lower) return v;
  }
  return capitalizeWordsForFacet(trimmed);
}

export function resolvePeopleManagementFromUrl(value: string, locale: 'en' | 'fi' | 'sv'): string {
  const trimmed = value.trim();
  if (!trimmed) return trimmed;
  const lower = trimmed.toLowerCase();
  for (const v of VALID_PEOPLE_MANAGEMENT[locale]) {
    if (v.toLowerCase() === lower) return v;
  }
  const enMatch = VALID_PEOPLE_MANAGEMENT.en.find((v) => v.toLowerCase() === lower);
  if (enMatch) {
    if (locale === 'fi' && PEOPLE_MGMT_EN_TO_FI[enMatch]) return PEOPLE_MGMT_EN_TO_FI[enMatch];
    if (locale === 'sv' && PEOPLE_MGMT_EN_TO_SV[enMatch]) return PEOPLE_MGMT_EN_TO_SV[enMatch];
    if (locale === 'en') return enMatch;
  }
  return capitalizeWordsForFacet(trimmed);
}

export function resolveLanguageNameFromUrl(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return trimmed;
  const lower = trimmed.toLowerCase();
  for (const name of VALID_LANGUAGE_NAMES) {
    if (name.toLowerCase() === lower) return name;
  }
  return capitalizeWordsForFacet(trimmed);
}

/**
 * Normalizes a role level value. If the value is an English enum in a non-English
 * locale field, maps it to the correct locale equivalent.
 */
function normalizeRoleLevel(value: string | undefined, locale: 'en' | 'sv' | 'fi'): string | undefined {
  if (!value || typeof value !== 'string') return undefined;

  const trimmed = value.trim();
  if (!trimmed) return undefined;

  const validValues = VALID_ROLE_LEVELS[locale];

  if (validValues.includes(trimmed as any)) return trimmed;

  // Cross-locale recovery: if the value is an English enum, map to target locale
  if (locale === 'fi' && ROLE_LEVEL_EN_TO_FI[trimmed]) return ROLE_LEVEL_EN_TO_FI[trimmed];
  if (locale === 'sv' && ROLE_LEVEL_EN_TO_SV[trimmed]) return ROLE_LEVEL_EN_TO_SV[trimmed];

  return undefined;
}

/**
 * Normalizes a people management tier value. If the value is an English enum in a
 * non-English locale field, maps it to the correct locale equivalent.
 */
function normalizePeopleManagement(value: string | undefined, locale: 'en' | 'sv' | 'fi'): string | undefined {
  if (!value || typeof value !== 'string') return undefined;

  const trimmed = value.trim();
  if (!trimmed) return undefined;

  const validValues = VALID_PEOPLE_MANAGEMENT[locale];

  if (validValues.includes(trimmed as any)) return trimmed;

  // Cross-locale recovery: if the value is an English enum, map to target locale
  if (locale === 'fi' && PEOPLE_MGMT_EN_TO_FI[trimmed]) return PEOPLE_MGMT_EN_TO_FI[trimmed];
  if (locale === 'sv' && PEOPLE_MGMT_EN_TO_SV[trimmed]) return PEOPLE_MGMT_EN_TO_SV[trimmed];

  return undefined;
}

// Proficiency/filler words that sometimes wrap a language name in extracted text (e.g.
// "Fluent Finnish", "Native English speaker", "Knowledge of German (B2)") — stripped so the
// underlying language can still resolve to a VALID_LANGUAGE_NAMES entry instead of being
// rejected outright. Deliberately does NOT touch phrases like "Scandinavian languages" or
// "EU languages" — those stay rejected by the invalidPatterns check in isValidLanguageName.
const LANGUAGE_QUALIFIER_WORDS = new Set([
  'fluent', 'native', 'basic', 'intermediate', 'advanced', 'professional', 'working',
  'conversational', 'beginner', 'elementary', 'proficient', 'good', 'excellent', 'speaker',
  'speakers', 'mother', 'tongue', 'level', 'proficiency', 'knowledge', 'skills', 'of', 'in',
]);

function stripLanguageQualifiers(value: string): string {
  const withoutParens = value.replace(/\([^)]*\)/g, ' '); // drop "(C1)", "(native)"
  const core = withoutParens
    .split(/\s+/)
    .filter(Boolean)
    .filter((w) => !LANGUAGE_QUALIFIER_WORDS.has(w.toLowerCase()));
  return core.join(' ').trim();
}

/**
 * Normalizes a language name to correct common typos and variations
 */
export function normalizeLanguageName(language: string): string {
  if (!language || typeof language !== 'string') return language;

  const trimmed = language.trim();
  if (!trimmed) return language;

  // First, check if it's already in our valid set with correct casing
  if (VALID_LANGUAGE_NAMES.has(trimmed)) {
    return trimmed;
  }

  // Check case-insensitive match in valid set and return with correct casing
  const lowerTrimmed = trimmed.toLowerCase();
  for (const validLang of VALID_LANGUAGE_NAMES) {
    if (validLang.toLowerCase() === lowerTrimmed) {
      return validLang;
    }
  }

  // Common typos and variations mapping (only for actual typos, not case variations)
  // Key: lowercase version of the typo
  // Value: correct language name
  const languageTypos: Record<string, string> = {
    // Swedish typos
    'finnska': 'Finska',  // Common typo: double 'n' instead of single 'n'
  };

  // Check for known typos
  if (languageTypos[lowerTrimmed]) {
    return languageTypos[lowerTrimmed];
  }

  // Try stripping proficiency/filler qualifiers and re-matching (e.g. "Fluent Finnish" -> "Finnish").
  const stripped = stripLanguageQualifiers(trimmed);
  if (stripped && stripped !== trimmed) {
    if (VALID_LANGUAGE_NAMES.has(stripped)) return stripped;
    const strippedLower = stripped.toLowerCase();
    for (const validLang of VALID_LANGUAGE_NAMES) {
      if (validLang.toLowerCase() === strippedLower) return validLang;
    }
  }

  // If no normalization found, return original (will be validated later)
  return trimmed;
}

/**
 * Validates that a language name is a valid language (not a phrase like "Scandinavian languages")
 */
export function isValidLanguageName(language: string): boolean {
  if (!language || typeof language !== 'string') return false;
  
  const trimmed = language.trim();
  if (!trimmed) return false;
  
  // Check if it's in our valid language names set
  if (VALID_LANGUAGE_NAMES.has(trimmed)) {
    return true;
  }
  
  // Check case-insensitive
  for (const validLang of VALID_LANGUAGE_NAMES) {
    if (validLang.toLowerCase() === trimmed.toLowerCase()) {
      return true;
    }
  }
  
  // Reject common invalid patterns
  const invalidPatterns = [
    /languages?$/i,  // "Scandinavian languages", "European languages"
    /language groups?/i,
    /dialects?/i,
    /varieties?/i,
  ];
  
  for (const pattern of invalidPatterns) {
    if (pattern.test(trimmed)) {
      return false;
    }
  }
  
  // If it's a single word and looks like a language name, allow it
  // (covers languages we might not have in our list)
  if (/^[A-ZÄÖÅ][a-zäöå]+$/.test(trimmed)) {
    return true;
  }
  
  return false;
}

/**
 * Normalizes an education level value to a valid enum value
 */
function normalizeEducationLevel(value: string | undefined, locale: 'en' | 'sv' | 'fi'): string | undefined {
  if (!value || typeof value !== 'string') return undefined;
  
  const validValues = VALID_EDUCATION_LEVELS[locale];
  const lowerValue = value.toLowerCase().trim();
  
  // Direct match
  if (validValues.includes(value as any)) {
    return value;
  }
  
  // Case-insensitive match
  for (const validValue of validValues) {
    if (validValue.toLowerCase() === lowerValue) {
      return validValue;
    }
  }
  
  // Common variations mapping
  const variations: Record<string, Record<string, string>> = {
    en: {
      'none required': 'No degree required',
      'no education required': 'No degree required',
      'no degree required': 'No degree required',
      'high school': 'High school',
      'bachelor': 'Bachelor',
      "bachelor's": 'Bachelor',
      "bachelor's degree": 'Bachelor',
      'masters': 'Masters',
      "master's": 'Masters',
      "master's degree": 'Masters',
      'doctorate': 'Doctorate',
      'phd': 'Doctorate',
      'apprenticeship': 'Apprenticeship',
      'diploma': 'Diploma',
    },
    sv: {
      'ingen krävs': 'Ingen utbildning krävs',
      'ingen utbildning krävs': 'Ingen utbildning krävs',
      'gymnasieutbildning': 'Gymnasieutbildning',
      'kandidat': 'Kandidat',
      'masters': 'Masters',
      'doktorand': 'Doktorand',
      'lärlingsutbildning': 'Lärlingsutbildning',
      'diplom': 'Diplom',
    },
    fi: {
      'ei vaadita': 'Ei koulutusta vaadita',
      'ei koulutusta vaadita': 'Ei koulutusta vaadita',
      'toisen asteen koulutus': 'Toisen asteen koulutus',
      'alempi korkeakoulututkinto': 'Alempi korkeakoulututkinto',
      'ylempi korkeakoulututkinto': 'Ylempi korkeakoulututkinto',
      'tieteellinen jatkotutkinto': 'Tieteellinen jatkotutkinto',
      'oppisopimuskoulutus': 'Oppisopimuskoulutus',
      'diplomi': 'Diplomi',
    },
  };
  
  const normalized = variations[locale]?.[lowerValue];
  if (normalized) {
    return normalized;
  }
  
  // If no match found, return undefined (invalid value)
  return undefined;
}

/**
 * Validates and normalizes all job fields
 */
export function validateJobFields(job: JobWithValidatableFields): void {
  normalizeStandaloneRemoteInLocationArrays(job);
  filterBenefitsToFinlandWhitelist(job);

  // Validate remoteness fields
  if (job.remoteness_en !== undefined) {
    job.remoteness_en = normalizeRemotenessForLocale(job.remoteness_en, 'en') || undefined;
  }
  if (job.remoteness_sv !== undefined) {
    job.remoteness_sv = normalizeRemotenessForLocale(job.remoteness_sv, 'sv') || undefined;
  }
  if (job.remoteness_fi !== undefined) {
    job.remoteness_fi = normalizeRemotenessForLocale(job.remoteness_fi, 'fi') || undefined;
  }

  // Validate job type fields
  if (job.jobType_en !== undefined) {
    job.jobType_en = normalizeJobTypeForLocale(job.jobType_en, 'en') || undefined;
  }
  if (job.jobType_sv !== undefined) {
    job.jobType_sv = normalizeJobTypeForLocale(job.jobType_sv, 'sv') || undefined;
  }
  if (job.jobType_fi !== undefined) {
    job.jobType_fi = normalizeJobTypeForLocale(job.jobType_fi, 'fi') || undefined;
  }
  
  // Validate normalised role level fields
  if (job.normalisedRoleLevel_en !== undefined) {
    job.normalisedRoleLevel_en = normalizeRoleLevel(job.normalisedRoleLevel_en, 'en') || undefined;
  }
  if (job.normalisedRoleLevel_sv !== undefined) {
    job.normalisedRoleLevel_sv = normalizeRoleLevel(job.normalisedRoleLevel_sv, 'sv') || undefined;
  }
  if (job.normalisedRoleLevel_fi !== undefined) {
    job.normalisedRoleLevel_fi = normalizeRoleLevel(job.normalisedRoleLevel_fi, 'fi') || undefined;
  }

  // Validate people management tier fields
  if (job.peopleManagementTier_en !== undefined) {
    job.peopleManagementTier_en = normalizePeopleManagement(job.peopleManagementTier_en, 'en') || undefined;
  }
  if (job.peopleManagementTier_sv !== undefined) {
    job.peopleManagementTier_sv = normalizePeopleManagement(job.peopleManagementTier_sv, 'sv') || undefined;
  }
  if (job.peopleManagementTier_fi !== undefined) {
    job.peopleManagementTier_fi = normalizePeopleManagement(job.peopleManagementTier_fi, 'fi') || undefined;
  }

  // Validate and normalize language fields (normalize first, then remove invalid entries)
  const languageFields = [
    'requiredLanguages_en',
    'requiredLanguages_sv',
    'requiredLanguages_fi',
    'optionalLanguages_en',
    'optionalLanguages_sv',
    'optionalLanguages_fi',
  ] as const;
  
  for (const field of languageFields) {
    const raw = job[field];
    if (typeof raw === 'string' && raw.trim()) {
      job[field] = [raw.trim()] as any;
    } else if (raw != null && !Array.isArray(raw)) {
      job[field] = [] as any;
    } else if (raw === null) {
      job[field] = [] as any;
    }
    // undefined → field was absent; leave it untouched
    if (job[field] && Array.isArray(job[field])) {
      // First normalize each language name, then filter out invalid ones
      job[field] = (job[field] as string[])
        .map((lang: string) => normalizeLanguageName(lang))
        .filter((lang: string) => isValidLanguageName(lang));
    }
  }
  
  // Validate education level fields
  if (job.educationLevel_en !== undefined) {
    job.educationLevel_en = normalizeEducationLevel(job.educationLevel_en, 'en') || undefined;
  }
  if (job.educationLevel_sv !== undefined) {
    job.educationLevel_sv = normalizeEducationLevel(job.educationLevel_sv, 'sv') || undefined;
  }
  if (job.educationLevel_fi !== undefined) {
    job.educationLevel_fi = normalizeEducationLevel(job.educationLevel_fi, 'fi') || undefined;
  }

  normalizeJobFieldLabelsOnJob(job);
}
